import clsx from 'clsx';
import type { Circular } from '@/lib/types';
import { CircularItem } from './CircularItem';

const UL = 'm-0 list-none border-t border-rule-strong p-0';

/** Placeholder rows while the circulars load. */
export function ListSkeleton({ rows, layout, className }: { rows: number; layout: 'compact' | 'full'; className?: string }) {
  return (
    <>
      <p role="status" className="sr-only">Loading notices…</p>
      <ul aria-hidden="true" className={clsx(UL, layout === 'full' && 'border-t-0', className)}>
        {Array.from({ length: rows }, (_, i) => (
          <li
            key={i}
            className={clsx(
              'flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-rule',
              layout === 'full' ? 'px-6 py-[18px]' : 'py-4',
            )}
          >
            <span className="block h-3 w-[104px] bg-rule-subtle" />
            <span className="block h-3 w-[112px] bg-rule-subtle" />
            <span className="block h-[18px] w-[50%] bg-rule-subtle" />
          </li>
        ))}
      </ul>
    </>
  );
}

export function ListError({ onRetry, className }: { onRetry: () => void; className?: string }) {
  return (
    <div className={className}>
      <p className="m-0 text-[1.0625rem] text-muted">Notices could not be loaded. Please try again later.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 box-content min-h-11 cursor-pointer rounded-control border-[1.5px] border-navy-800 bg-white px-[22px] font-semibold text-navy-800"
      >
        Try again
      </button>
    </div>
  );
}

interface Props {
  status: 'loading' | 'error' | 'success';
  items: Circular[]; // already filtered / searched / capped by the caller
  totalCount: number; // before filtering, to tell "none yet" from "none match"
  layout: 'compact' | 'full';
  skeletonRows: number;
  onRetry: () => void;
}

// The Home card's list. (The Notices page lays out its own groups and uses
// ListSkeleton / ListError / CircularItem directly.)
export function CircularsList({ status, items, totalCount, layout, skeletonRows, onRetry }: Props) {
  const top = layout === 'full' ? 'mt-6' : 'mt-5';
  if (status === 'loading') return <ListSkeleton rows={skeletonRows} layout={layout} className={top} />;
  if (status === 'error') return <ListError onRetry={onRetry} className={`${top} border-t border-rule-strong pt-6`} />;

  return (
    <>
      <ul aria-label="Circulars" className={clsx(UL, top)}>
        {items.map((c) => (
          <CircularItem key={c.id} circular={c} layout={layout} />
        ))}
      </ul>
      {items.length === 0 && (
        <p className="mt-6 text-[1.0625rem] text-muted">
          {totalCount === 0 ? 'No notices have been published yet.' : 'No notices match your search.'}
        </p>
      )}
    </>
  );
}
